import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Play, Check, Euro } from 'lucide-react';
import { Link } from 'react-router-dom';

export const AgentCard = ({ agent }) => {
  const { t } = useLanguage();
  const [videoOpen, setVideoOpen] = useState(false);

  const getVideoEmbed = () => {
    if (!agent.demo_video_url) return null;
    
    if (agent.demo_video_type === 'youtube') {
      return (
        <iframe
          src={agent.demo_video_url}
          className="w-full aspect-video rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={agent.name}
        />
      );
    }
    
    if (agent.demo_video_type === 'vimeo') {
      return (
        <iframe
          src={agent.demo_video_url}
          className="w-full aspect-video rounded-lg"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={agent.name}
        />
      );
    }

    // Local video
    return (
      <video
        src={agent.demo_video_url}
        className="w-full aspect-video rounded-lg"
        controls
      />
    );
  };

  return (
    <Card className="card-hover bg-white border-slate-100 overflow-hidden" data-testid={`agent-card-${agent.id}`}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <CardTitle className="font-heading text-xl text-slate-900">{agent.name}</CardTitle>
          <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
            <Euro className="w-4 h-4" />
            {agent.price}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-slate-600 text-sm leading-relaxed">{agent.description}</p>
        
        {/* Features */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('features')}</h4>
          <ul className="space-y-1">
            {agent.features?.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          {agent.demo_video_url && (
            <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex-1 gap-2" data-testid={`agent-demo-btn-${agent.id}`}>
                  <Play className="w-4 h-4" />
                  {t('viewDemo')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>{agent.name} - {t('demoVideo')}</DialogTitle>
                </DialogHeader>
                {getVideoEmbed()}
              </DialogContent>
            </Dialog>
          )}
          <Link to={`/register?agent=${agent.id}&branch=${agent.branch}`} className="flex-1">
            <Button className="w-full bg-primary hover:bg-primary-hover text-white gap-2" data-testid={`agent-cta-btn-${agent.id}`}>
              {t('getStarted')}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
